/**
 * users.service.ts — Layer 3: Users Domain
 *
 * All user-profile and user-management API calls live here.
 * Components and hooks import ONLY from this file.
 */

import { getRequest, postRequest, putRequest, patchRequest, deleteRequest, postFormData } from './http.service';
import {
  URL_USERS,
  URL_USERS_LIST,
  URL_USERS_PROFILE,
  URL_USERS_SIGNATURE,
  URL_USERS_INVITE,
  URL_USERS_ACCEPT_INVITE,
  URL_USERS_REQUEST_PASSWORD_OTP,
  URL_USERS_CHANGE_PASSWORD_OTP,
  URL_USER_BY_ID,
  URL_USER_RESEND_INVITE,
  URL_PERSONAL_DETAILS,
  URL_ORG_USERS,
  URL_USERS_PRORATED_AMOUNT,
  URL_USERS_INVITE_INFO,
} from './url.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InviteUserPayload {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  orgId?: string;
}

export interface UpdateUserPayload {
  firstName?: string;
  lastName?: string;
  role?: string;
  [key: string]: unknown;
}

export interface ChangePasswordPayload {
  userId: string;
  otp: string;
  newPassword: string;
}

// ---------------------------------------------------------------------------
// Listing & fetching
// ---------------------------------------------------------------------------

/** Get all users in the current org */
export const fetchUsers = () =>
  getRequest(URL_USERS);

/** Get advisors (name + email fields) filtered by org — used for meeting scheduler */
export const fetchAdvisors = (orgId: string) =>
  getRequest(`${URL_USERS}?select=firstName+lastName+email+_id+orgId&orgId=${orgId}`);

/** Get a paginated/filtered list of users */
export const fetchUsersList = (orgId?: string) =>
  getRequest(orgId ? `${URL_USERS_LIST}?orgId=${encodeURIComponent(orgId)}` : URL_USERS_LIST);

/** Get the current user's profile */
export const fetchProfile = () =>
  getRequest(URL_USERS_PROFILE);

/** Get a single user by ID */
export const fetchUserById = (userId: string) =>
  getRequest(URL_USER_BY_ID(userId));

/** Get all personal details for an org (used to resolve profile images) */
export const fetchPersonalDetails = (orgId: string) =>
  getRequest(URL_PERSONAL_DETAILS(orgId));

/** Get users belonging to multiple orgs (for global-admin views) */
export const fetchOrgUsers = (orgIds: string) =>
  getRequest(URL_ORG_USERS(orgIds));

// ---------------------------------------------------------------------------
// Creating & inviting
// ---------------------------------------------------------------------------

/** Invite a new user to the org */
export const inviteUser = (payload: InviteUserPayload) =>
  postRequest(URL_USERS_INVITE, payload as Record<string, unknown>);

/** Get the org/first-name info shown on the accept-invite page */
export const fetchInviteInfo = (token: string) =>
  getRequest(URL_USERS_INVITE_INFO(token));

/** Accept an invite using the invite token */
export const acceptInvite = (token: string, password: string, confirmPassword: string) =>
  postRequest(URL_USERS_ACCEPT_INVITE, { token, password, confirmPassword });

// ---------------------------------------------------------------------------
// Updating
// ---------------------------------------------------------------------------

/** Update a user's details */
export const updateUser = (userId: string, payload: UpdateUserPayload) =>
  putRequest(URL_USER_BY_ID(userId), payload as Record<string, unknown>);

/** Upload / replace the user's signature image */
export const uploadSignature = (formData: FormData) =>
  postFormData(URL_USERS_SIGNATURE, formData);

/** Get the current user's email signature (HTML + enabled flag) */
export const fetchSignature = () =>
  getRequest(URL_USERS_SIGNATURE);

/** Update the current user's profile (name, signature, experimental features, etc.) */
export const updateProfile = (payload: Record<string, unknown>) =>
  patchRequest(URL_USERS_PROFILE, payload);

// ---------------------------------------------------------------------------
// Deleting
// ---------------------------------------------------------------------------

/** Remove a user from the org */
export const deleteUser = (userId: string) =>
  deleteRequest(URL_USER_BY_ID(userId));

// ---------------------------------------------------------------------------
// Invite management
// ---------------------------------------------------------------------------

/** Resend the invite email for a pending user */
export const resendUserInvite = (userId: string) =>
  postRequest(URL_USER_RESEND_INVITE(userId), {});

// ---------------------------------------------------------------------------
// Password (OTP flow)
// ---------------------------------------------------------------------------

/** Request a one-time password for changing the account password */
export const requestPasswordOTP = (userId: string) =>
  postRequest(URL_USERS_REQUEST_PASSWORD_OTP, { userId });

/** Change the password using the OTP received by email */
export const changePasswordWithOTP = (payload: ChangePasswordPayload) =>
  postRequest(URL_USERS_CHANGE_PASSWORD_OTP, payload as Record<string, unknown>);

/** Create one or more users directly (admin/manual-password flow) */
export const createUsers = (body: unknown[]) =>
  postRequest(URL_USERS, body as unknown[]);

/** Get the prorated charge for adding N seats to an org */
export const fetchProratedAmount = (orgId: string, count: number) =>
  getRequest(URL_USERS_PRORATED_AMOUNT(orgId, count));
